<script setup lang="ts">
import { toChartVarName } from "@/lib/utils";
import { eventBus } from "#bb/lib/eventBus";

import { VisSingleContainer } from "@unovis/vue";
import { THEMES } from "~/components/ui/chart";

const logger = useLogger("Container");

/** 1) Expect 'id' from the parent */
const props = defineProps<{ id: string }>();

/** 2) Get access to the store's read method only */
const { getContentById } = useContentManager();

/**
 * 3) Read-only computed property wrapping the container definition data.
 */
const definitionData = computed(() => getContentById(props.id));

/** 5) Inject features (editor mode) */
const features = inject("render-features", { editor: false });

/** 6) Commands for context menu (optional) */
const commands = [
  {
    id: "editors-group",
    type: "group",
    name: "Editors",
    description: "Single Container editors",
    commands: [
      {
        id: "edit",
        name: "Edit",
        description: "Edit this container",
        action: async () => {
          // @ts-ignore - dynamic event name not in mitt typed Events
          eventBus.emit(`component:onEdit:${props.id}`, {
            data: definitionData.value,
          });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

/** 7) Expose commands & properties for the resource editor / context menu */
defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "Single Container",
    icon: "lucide:box-select",
  },
});

// Bring in data exactly as with XY container parity (with loading flags)
type InjectedReport = { data: Array<Record<string, any>>; loading?: boolean; initialized?: boolean };
const report = inject<InjectedReport>("report", { data: [], loading: false, initialized: false });
const chartData = computed(() => (report.loading || !report.initialized ? [] : report.data));
const containerHeight = computed(() => {
  const height = Number(definitionData.value?.height);
  return Number.isFinite(height) && height > 0 ? height : 400;
});

const rawChartConfig = computed(() => {
  const config = definitionData.value?.chartConfig;
  if (typeof config === "string") {
    const code = config.trim();
    if (!code)
      return {};
    try {
      return new Function(`return (${code})`)();
    } catch {
      try {
        return new Function(code)();
      } catch (err) {
        console.error("Failed to eval chartConfig", err);
        return {};
      }
    }
  }
  return config && typeof config === "object" ? config : {};
});

const chartConfig = computed<ChartConfig>(() => {
  const mapped: ChartConfig = {};
  for (const [key, item] of Object.entries(rawChartConfig.value)) {
    const varName = toChartVarName(key);
    const hasTheme = !!item?.theme;
    const hasColor = !!item?.color;
    const color = (hasTheme || hasColor) && varName ? `var(--color-${varName})` : undefined;

    mapped[key] = {
      ...(item?.label ? { label: item.label } : {}),
      ...(item?.icon ? { icon: item.icon } : {}),
      ...(color ? { color } : {}),
    };
  }

  return mapped;
});

const chartStyleText = computed(() => {
  const entries = Object.entries(rawChartConfig.value);
  if (!entries.length)
    return "";

  const chartId = `chart-${props.id}`;

  return Object.entries(THEMES)
    .map(([theme, prefix]) => {
      const lines = entries
        .map(([key, item]) => {
          const varName = toChartVarName(key);
          if (!varName)
            return null;
          const themeColor = item?.theme?.[theme as keyof typeof THEMES] ?? item?.color;
          return themeColor ? `  --color-${varName}: ${themeColor};` : null;
        })
        .filter(Boolean)
        .join("\n");

      return lines ? `\n${prefix} [data-chart=${chartId}] {\n${lines}\n}\n` : "";
    })
    .filter(Boolean)
    .join("\n");
});
</script>

<template>
  <div class="min-w-0 w-full">
    <ClientOnly>
      <ChartContainer
        v-if="!features.editor"
        :id="props.id"
        :config="chartConfig"
        :height="containerHeight"
      >
        <component v-if="chartStyleText" :is="'style'">
          {{ chartStyleText }}
        </component>
        <VisSingleContainer :data="chartData" :height="containerHeight">
          <slot />
        </VisSingleContainer>
      </ChartContainer>
      <VisSingleContainer v-else :data="chartData" :height="containerHeight">
        <slot />
      </VisSingleContainer>
    </ClientOnly>
  </div>

</template>

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";
import type { ChartConfig } from "~/components/ui/chart";
export default {
  editor: () => import("#bb/components/editor/unovis/container/single.vue"),
  componentId: ComponentDefinitionIds.UNOVIS_SINGLE_CONTAINER,
  id: "singlecontainer",
  icon: "lucide:box-select",
  type: "Single Container",
  description:
    "Single Container is a basic container for a Unovis component.",
  default: {
    columnsSettings: {
      default: 1,
    },
  },
  categories: ["UNOVIS"],
  order: 12004,
};
</script>
